import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  type RouteObject,
} from "react-router";
import { AppShell } from "@/app/layouts/AppShell";
import { AuthLayout } from "@/app/layouts/AuthLayout";
import { RequireAdmin, RequireAuth } from "@/features/auth";
import { ForbiddenPage } from "@/pages/ForbiddenPage";
import { RouteError } from "./RouteError";

const page = <T extends string>(load: () => Promise<Record<T, React.ComponentType>>, name: T) => ({
  lazy: async () => ({ Component: (await load())[name] }),
});

/** Exported separately so tests can mount the real route table in a memory router. */
export const routes: RouteObject[] = [
  {
    path: "/login",
    element: <AuthLayout />,
    errorElement: <RouteError />,
    children: [{ index: true, ...page(() => import("@/pages/LoginPage"), "LoginPage") }],
  },
  {
    path: "/auth/zoho",
    element: <AuthLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, ...page(() => import("@/pages/ZohoCompletePage"), "ZohoCompletePage") },
    ],
  },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, ...page(() => import("@/pages/DashboardPage"), "DashboardPage") },
      { path: "drive", ...page(() => import("@/pages/DrivePage"), "DrivePage") },
      { path: "drive/f/:folderId", ...page(() => import("@/pages/DrivePage"), "DrivePage") },
      // Older links carried a bucket ("/drive/xmlfiles/f/<id>"); there is only one drive now, so it is ignored.
      { path: "drive/:bucket", element: <Navigate to="/drive" replace /> },
      {
        path: "drive/:bucket/f/:folderId",
        ...page(() => import("@/pages/DrivePage"), "DrivePage"),
      },
      { path: "shared", ...page(() => import("@/pages/SharedPage"), "SharedPage") },
      { path: "trash", ...page(() => import("@/pages/TrashPage"), "TrashPage") },
      { path: "search", ...page(() => import("@/pages/SearchPage"), "SearchPage") },
      { path: "settings/security", ...page(() => import("@/pages/SecurityPage"), "SecurityPage") },
      {
        path: "admin",
        element: (
          <RequireAdmin fallback={<ForbiddenPage />}>
            <Outlet />
          </RequireAdmin>
        ),
        children: [
          { path: "users", ...page(() => import("@/pages/AdminUsersPage"), "AdminUsersPage") },
          { path: "audit", ...page(() => import("@/pages/AdminAuditPage"), "AdminAuditPage") },
        ],
      },
      { path: "*", ...page(() => import("@/pages/NotFoundPage"), "NotFoundPage") },
    ],
  },
];

export const router = createBrowserRouter(routes);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
